import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { tapi, apiErr } from "../../lib/tenant";
import { trackScoreComplete } from "../../lib/ga";

const PHASES = ["Reading your documents…", "Checking revenue and cash flow…", "Assessing repayment capacity…", "Calculating your Capital Readiness Score…"];
const LPO_PHASES = ["Reading your LPO and company documents…", "Checking your delivery track record…", "Reviewing your financial records…", "Working out whether you qualify…"];
const COUNTDOWN_SECS = 210; // 3:30
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const Countdown = ({ running, overtimeNote }) => {
  const [left, setLeft] = useState(COUNTDOWN_SECS);
  useEffect(() => {
    if (!running) return undefined;
    setLeft(COUNTDOWN_SECS);
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [running]);
  const pct = ((COUNTDOWN_SECS - left) / COUNTDOWN_SECS) * 100;
  return (
    <div className="mx-auto max-w-xs" data-testid="score-countdown">
      <p className="text-3xl font-extrabold tabular-nums tracking-tight" style={{ color: "var(--tenant)" }} data-testid="score-countdown-time">{mmss(left)}</p>
      <p className="mt-0.5 text-[11px] text-neutral-400">{left > 0 ? "estimated time remaining" : "almost there — finishing up"}</p>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
        <div className="h-full rounded-full transition-[width] duration-1000 ease-linear" style={{ width: `${pct}%`, background: "var(--tenant)" }} />
      </div>
      {left === 0 && overtimeNote && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="score-overtime-note">{overtimeNote}</p>
      )}
    </div>
  );
};

export const StepScore = ({ me, onBack, onScored, rerun = false, autoStart = false }) => {
  const { sub, config } = useTenant();
  const lpo = config.prequal_flow === "lpo";
  const brand = lpo ? (config.eyebrow || "").split("·")[0].trim() || config.name : config.name;
  const phases = lpo ? LPO_PHASES : PHASES;
  const [state, setState] = useState(me.inflight_job ? "running" : me.score != null && !rerun ? "done" : "idle");
  const [jobId, setJobId] = useState(me.inflight_job?.job_id || null);
  const [phase, setPhase] = useState(0);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (state !== "running") return undefined;
    const t = setInterval(() => setPhase((p) => (p + 1) % phases.length), 6000);
    return () => clearInterval(t);
  }, [state, phases.length]);

  const poll = async (id) => {
    try {
      const { data } = await tapi.get(`/ai/analyze/status/${id}`);
      if (data.status === "done") { trackScoreComplete(data.new_score ?? data.analysis?.readiness_score, { score_type: "tenant_score", tenant: sub }); setState("done"); onScored(); return; }
      if (data.status === "failed") { setState("error"); setError(data.error || "Scoring failed. Please try again."); return; }
    } catch (_e) { /* transient */ }
    timer.current = setTimeout(() => poll(id), 4000);
  };
  useEffect(() => { if (jobId && state === "running") poll(jobId); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoStart && state === "idle") start(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (attempt = 0) => {
    setState("running"); setError("");
    try {
      const { data } = await tapi.post(`/t/${sub}/score`);
      if (data.status === "done") { trackScoreComplete(data.score, { score_type: "tenant_score", tenant: sub }); setState("done"); onScored(); return; }
      setJobId(data.job_id); poll(data.job_id);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) { setState("error"); setError(e.response?.data?.detail?.message || "You have used all your re-runs."); return; }
      if (status === 409 && attempt < 20) { timer.current = setTimeout(() => start(attempt + 1), 6000); return; }
      setState("error"); setError(apiErr(e, "We couldn't score your business yet."));
      if (status !== 409) toast.error(apiErr(e, "Scoring failed"));
    }
  };

  return (
    <div className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm" data-testid="tenant-step-score">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: "var(--tenant)" }}><Sparkles size={26} /></span>
      <div><h2 className="text-lg font-extrabold" data-testid="score-heading">{lpo ? "Thank you for uploading your documents" : "We score your business"}</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-neutral-500" data-testid="score-subheading">
          {lpo
            ? <>Please hold — we're checking your application and will tell you whether you qualify for a <b>{brand} loan</b> within 1–3 minutes.</>
            : <>We read your documents and your answers, then produce a Capital Readiness Score and repayment-capacity check against {config.name}'s criteria. This takes 1–3 minutes.</>}
        </p></div>
      {state === "idle" && <BrandButton onClick={() => start()} data-testid="score-start">{rerun ? "Re-score my business" : "Score my business"}</BrandButton>}
      {state === "running" && (
        <div className="space-y-3" data-testid="score-running">
          <Countdown running={state === "running"} overtimeNote={lpo ? `This is taking a little longer than usual. You can safely close this page — we'll email your result to ${me.user?.email || "you"} as soon as it's ready.` : null} />
          <p className="inline-flex items-center gap-2 text-sm font-semibold"><Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--tenant)" }} />{phases[phase]}</p>
          <p className="text-[11px] text-neutral-400">Please keep this page open.</p>
        </div>
      )}
      {state === "error" && (
        <div className="space-y-3" data-testid="score-error"><p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p><BrandButton onClick={() => start()}>Try again</BrandButton></div>
      )}
      {state === "done" && <p className="text-sm font-semibold text-emerald-700" data-testid="score-done">Scoring complete — preparing your outcome…</p>}
      <button type="button" onClick={onBack} disabled={state === "running"} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-neutral-800 disabled:opacity-40" data-testid="score-back"><ArrowLeft size={14} /> Back to documents</button>
    </div>
  );
};
